import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2 } from "lucide-react";

import { DetailLinkWithEntityPeek } from "@/components/hub/DetailLinkWithEntityPeek";
import { useEntityPeekPrefetchHandlers } from "@/components/hub/EntityPeekHint";
import type { EntityPeekProps } from "@/components/hub/entityPeekTypes";
import { PurchaseOrderDetailLink, WorkOrderDetailLink } from "@/components/hub/HubEntityDetailLink";
import { ZplLocationSelect } from "@/components/ZplLocationSelect";
import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { buildInboundReceiptReviewPath } from "@/lib/hubEntityDetailPaths";
import { ApiError } from "@/lib/api";
import { formatQuantityWithUnit } from "@/lib/mrpSkuUnitDisplay";
import { usePageBreadcrumb } from "@/lib/navigationTrailContext";

import {
  approveReceiptOverageAsFree,
  fetchReceiptDiscrepancyPurchaseOrders,
  formatReceiptDiscrepancyDate,
  formatReceiptDiscrepancyMoney,
  formatReceiptDiscrepancyQuantity,
  allowReceiptDiscrepancyExcessMaterial,
  overrideReceiptQuantities,
  receiptDiscrepancyPurchaseOrderMatches,
  refuseReceiptDiscrepancyExcessPayment,
  receiptMatchStatusLabel,
  receiptStatusLabel,
  vendorInvoiceCanAcceptOverage,
  type ReceiptDiscrepancyDifference,
  type ReceiptDiscrepancyHoldReason,
  type ReceiptDiscrepancyLine,
  type ReceiptDiscrepancyPurchaseOrder,
  type ReceiptDiscrepancyReceipt,
  type ReceiptDiscrepancyWorkOrder,
} from "./receiptDiscrepancyApi";
import { useInboundZplPrinter } from "@/pages/shipping/inboundZplPrinter";

export function ReceiptDiscrepancyPage(): JSX.Element {
  usePageBreadcrumb("Receipt discrepancy");
  const queryClient = useQueryClient();
  const [query, setQuery] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const listQuery = useQuery({
    queryKey: ["accounting", "receipt-discrepancy"],
    queryFn: fetchReceiptDiscrepancyPurchaseOrders,
  });

  const purchaseOrders = listQuery.data ?? [];
  const visible = useMemo(
    () => purchaseOrders.filter((purchaseOrder) => receiptDiscrepancyPurchaseOrderMatches(purchaseOrder, query)),
    [purchaseOrders, query],
  );
  const filterActive = query.trim() !== "";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <PageTitleWithHelp hoverHint="Purchase orders where the vendor invoice, receipt, ordered quantity, finished goods, or transfer do not agree. Contract manufacturer finished goods are posted from CM PO receiving.">
          Receipt discrepancy
        </PageTitleWithHelp>
      </div>

      <Card>
        <CardBody>
          <Label htmlFor="receipt-discrepancy-search">Search</Label>
          <Input
            id="receipt-discrepancy-search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Purchase order, supplier, or SKU"
            className="mt-1 max-w-md"
          />
        </CardBody>
      </Card>

      {notice ? <p className="text-sm text-emerald-800">{notice}</p> : null}

      {listQuery.isLoading ? (
        <p className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading receipt discrepancies...
        </p>
      ) : listQuery.isError ? (
        <p role="alert" className="flex items-center gap-2 text-sm text-red-600">
          <AlertTriangle className="h-4 w-4" />
          Could not load receipt discrepancies.{" "}
          <button type="button" className="underline" onClick={() => void listQuery.refetch()}>
            Retry
          </button>
        </p>
      ) : visible.length === 0 ? (
        <Card>
          <CardBody>
            <p className="text-sm text-slate-500">
              {filterActive
                ? "No rows match these filters."
                : "No purchase orders have a receipt discrepancy."}
            </p>
          </CardBody>
        </Card>
      ) : (
        <ul className="space-y-4">
          {visible.map((purchaseOrder) => (
            <li key={purchaseOrder.purchase_order_id}>
              <PurchaseOrderComparison
                purchaseOrder={purchaseOrder}
                onChanged={(message) => {
                  setNotice(message);
                  void queryClient.invalidateQueries({ queryKey: ["accounting", "receipt-discrepancy"] });
                }}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PurchaseOrderComparison({
  purchaseOrder,
  onChanged,
}: {
  purchaseOrder: ReceiptDiscrepancyPurchaseOrder;
  onChanged: (message: string) => void;
}): JSX.Element {
  const purchaseOrderId = Number(purchaseOrder.purchase_order_id);
  return (
    <Card>
      <CardBody className="space-y-4">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <PurchaseOrderDetailLink
            purchaseOrderId={purchaseOrderId}
            purchaseOrderNumber={purchaseOrder.purchase_order_number}
            className="text-base font-semibold"
          />
          {purchaseOrder.supplier_name ? (
            <span className="text-sm text-slate-600">{purchaseOrder.supplier_name}</span>
          ) : null}
        </div>

        {purchaseOrder.differences.length === 0 && purchaseOrder.hold_reasons.length === 0 ? (
          <p className="text-sm text-slate-600">
            Marked as a receipt discrepancy. Column totals match.
          </p>
        ) : (
          <DifferenceList
            differences={purchaseOrder.differences}
            holdReasons={purchaseOrder.hold_reasons}
            hasWorkOrders={purchaseOrder.has_work_orders}
          />
        )}

        {purchaseOrder.invoice_exceeds_purchase_order ? (
          <PurchaseOrderQuantityResolve
            purchaseOrderId={purchaseOrder.purchase_order_id}
            onResolved={onChanged}
          />
        ) : null}

        <div
          className={
            purchaseOrder.has_work_orders
              ? "grid grid-cols-1 gap-3 xl:grid-cols-3"
              : "grid grid-cols-1 gap-3 lg:grid-cols-2"
          }
        >
          <ComparisonColumn title="Vendor invoice">
            {purchaseOrder.vendor_invoices.length === 0 ? (
              <p className="text-sm text-slate-500">No vendor invoice.</p>
            ) : (
              purchaseOrder.vendor_invoices.map((invoice) => (
                <DocumentBlock
                  key={invoice.id}
                  title={
                    <VendorInvoicePeekLink
                      invoiceId={invoice.id}
                      label={invoice.vendor_invoice_number ?? `Invoice ${invoice.id}`}
                    />
                  }
                  meta={[
                    formatReceiptDiscrepancyDate(invoice.invoice_date),
                    receiptMatchStatusLabel(invoice.receipt_match_status),
                  ]}
                >
                  <LineList lines={invoice.lines} currency={invoice.currency} showMoney />
                  {vendorInvoiceCanAcceptOverage(invoice.receipt_match_status, purchaseOrder.differences) ? (
                    <AcceptOverageButton
                      invoiceId={invoice.id}
                      onAccepted={() =>
                        onChanged(
                          "Overage accepted as free product. GIPI quantity is capped at submitted receipt per line.",
                        )
                      }
                    />
                  ) : null}
                </DocumentBlock>
              ))
            )}
          </ComparisonColumn>

          <ComparisonColumn title="Receipt">
            {purchaseOrder.receipts.length === 0 ? (
              <p className="text-sm text-slate-500">No receipt.</p>
            ) : (
              purchaseOrder.receipts.map((receipt) => (
                <DocumentBlock
                  key={receipt.id}
                  title={<InboundReceiptPeekLink receiptId={receipt.id} />}
                  meta={[
                    receiptStatusLabel(receipt.status),
                    formatReceiptDiscrepancyDate(receipt.received_at),
                  ]}
                >
                  <LineList lines={receipt.lines} currency={null} showMoney={false} />
                  {receipt.quantity_override != null ? (
                    <p className="text-xs text-slate-600">
                      Overridden by {receipt.quantity_override.overridden_by_name ?? "an unknown user"} on{" "}
                      {formatReceiptDiscrepancyDate(receipt.quantity_override.overridden_at)}.{" "}
                      {receipt.quantity_override.reason}
                    </p>
                  ) : null}
                  <ReceiptQuantityOverrideButton receipt={receipt} onSaved={onChanged} />
                </DocumentBlock>
              ))
            )}
          </ComparisonColumn>

          {purchaseOrder.cm_finished_goods != null ? (
            <ComparisonColumn title="Finished goods">
              <p className="text-sm text-slate-700">
                {formatReceiptDiscrepancyQuantity(purchaseOrder.cm_finished_goods.wofg_posted_quantity)} posted
                {purchaseOrder.cm_finished_goods.stays_at_build_site
                  ? ". Stays at the contract manufacturer for the next work order."
                  : purchaseOrder.cm_finished_goods.transfer_count > 0
                    ? `. ${formatReceiptDiscrepancyQuantity(purchaseOrder.cm_finished_goods.transfer_count)} transferred to Smart Grid Solutions.`
                    : ". Transfer to Smart Grid Solutions is not posted."}
              </p>
              <Link
                className="mt-2 inline-block text-sm text-brand-700 hover:text-brand-900"
                to={`/manufacturing-management/cm-po/receiving?po=${encodeURIComponent(purchaseOrder.purchase_order_number ?? "")}`}
              >
                Post from CM PO receiving
              </Link>
            </ComparisonColumn>
          ) : null}

          {purchaseOrder.has_work_orders ? (
            <ComparisonColumn title="Work order">
              {purchaseOrder.work_orders.map((workOrder) => (
                <WorkOrderBlock key={workOrder.build_order_id} workOrder={workOrder} />
              ))}
            </ComparisonColumn>
          ) : null}
        </div>
      </CardBody>
    </Card>
  );
}

const HOLD_REASON_LABELS: Record<string, string> = {
  waiting_on_invoice: "Waiting on invoice",
  invoice_receipt_mismatch: "Invoice quantity mismatch",
  purchase_order_short: "Purchase order short",
  manufacturer_part_mismatch: "Manufacturer part mismatch",
  iqc: "IQC",
  damaged: "Damaged",
  sku_mismatch: "Wrong SKU",
  packing_slip_mismatch: "Packing slip mismatch",
  unknown_mpn: "Unknown manufacturer part",
  awaiting_wofg: "Waiting on finished goods",
};

function DifferenceList({
  differences,
  holdReasons,
  hasWorkOrders,
}: {
  differences: ReceiptDiscrepancyDifference[];
  holdReasons: ReceiptDiscrepancyHoldReason[];
  hasWorkOrders: boolean;
}): JSX.Element {
  return (
    <ul className="space-y-1 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-950">
      {holdReasons.map((hold) => (
        <li key={`hold-${hold.sku}`}>
          <span className="font-mono font-semibold">{hold.sku}</span>
          {" holds: "}
          {hold.reasons.map((reason) => HOLD_REASON_LABELS[reason] ?? reason).join(", ")}
        </li>
      ))}
      {differences.map((difference) => (
        <li key={difference.sku} className="flex flex-wrap gap-x-4 gap-y-1">
          <span className="font-mono font-semibold">{difference.sku}</span>
          {difference.ordered_quantity != null ? (
            <QuantityFact
              label="Purchase order"
              quantity={difference.ordered_quantity}
              unitOfMeasure={difference.unit_of_measure}
            />
          ) : null}
          <QuantityFact
            label="Vendor invoice"
            quantity={difference.vendor_invoice_quantity}
            unitOfMeasure={difference.unit_of_measure}
          />
          <QuantityFact
            label="Receipt"
            quantity={difference.receipt_quantity}
            unitOfMeasure={difference.unit_of_measure}
          />
          {hasWorkOrders && difference.work_order_quantity != null ? (
            <QuantityFact
              label="Work order"
              quantity={difference.work_order_quantity}
              unitOfMeasure={difference.unit_of_measure}
            />
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function QuantityFact({
  label,
  quantity,
  unitOfMeasure,
}: {
  label: string;
  quantity: number;
  unitOfMeasure: string | null;
}): JSX.Element {
  return (
    <span>
      {label}{" "}
      <span className="tabular-nums font-semibold">
        {formatQuantityWithUnit(formatReceiptDiscrepancyQuantity(quantity), unitOfMeasure)}
      </span>
    </span>
  );
}

function ComparisonColumn({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <section className="min-w-0 rounded-md border border-slate-200 bg-slate-50/60 p-3">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h2>
      <div className="mt-2 space-y-3">{children}</div>
    </section>
  );
}

function DocumentBlock({
  title,
  meta,
  children,
}: {
  title: ReactNode;
  meta: string[];
  children: ReactNode;
}): JSX.Element {
  const details = meta.filter((value) => value.trim() !== "");
  return (
    <div className="space-y-2">
      <div className="text-sm text-slate-900">
        {title}
        {details.length > 0 ? (
          <span className="text-slate-500"> · {details.join(" · ")}</span>
        ) : null}
      </div>
      {children}
    </div>
  );
}

function LineList({
  lines,
  currency,
  showMoney,
}: {
  lines: ReceiptDiscrepancyLine[];
  currency: string | null;
  showMoney: boolean;
}): JSX.Element {
  if (lines.length === 0) {
    return <p className="text-sm text-slate-500">No lines.</p>;
  }
  return (
    <ul className="space-y-1">
      {lines.map((line, index) => (
        <li
          key={`${line.sku}-${String(index)}`}
          className={
            line.differs
              ? "grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 rounded bg-amber-100/80 px-2 py-1 text-sm"
              : "grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 px-2 py-1 text-sm"
          }
        >
          <span className="min-w-0">
            <span className="font-mono text-slate-900">{line.sku}</span>
            {line.manufacturer_part != null ? (
              <span
                className={
                  line.part_differs
                    ? "ml-2 rounded bg-amber-100 px-1 font-mono text-amber-950"
                    : "ml-2 font-mono text-slate-600"
                }
              >
                {line.manufacturer_part}
              </span>
            ) : null}
            {line.is_fee ? <span className="ml-2 text-xs text-slate-500">fee</span> : null}
            {line.expected_quantity != null ? (
              <span className="ml-2 text-xs text-slate-500">
                expected {formatQuantityWithUnit(formatReceiptDiscrepancyQuantity(line.expected_quantity), line.unit_of_measure)}
              </span>
            ) : null}
          </span>
          <span className="text-right tabular-nums text-slate-800">
            {formatQuantityWithUnit(formatReceiptDiscrepancyQuantity(line.quantity), line.unit_of_measure)}
            {showMoney && line.unit_price != null
              ? ` @ ${formatReceiptDiscrepancyMoney(line.unit_price, currency)}`
              : ""}
            {showMoney && line.line_total != null
              ? ` · ${formatReceiptDiscrepancyMoney(line.line_total, currency)}`
              : ""}
          </span>
        </li>
      ))}
    </ul>
  );
}

function ReceiptQuantityOverrideButton({
  receipt,
  onSaved,
}: {
  receipt: ReceiptDiscrepancyReceipt;
  onSaved: (message: string) => void;
}): JSX.Element | null {
  const editableLines = receipt.lines.filter((line) => line.receipt_item_id != null && !line.is_fee);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [reprint, setReprint] = useState(true);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const { station, setStation, selectedPrinter, unavailableMessage } = useInboundZplPrinter({
    fetchPrintersEnabled: open,
  });
  const mutation = useMutation({
    mutationFn: () => {
      const lines = editableLines.flatMap((line) => {
        const itemId = line.receipt_item_id;
        if (itemId == null) return [];
        const quantity = Number(drafts[itemId] ?? String(line.quantity));
        return [{ inbound_receipt_item_id: itemId, quantity }];
      });
      return overrideReceiptQuantities({
        receiptId: receipt.id,
        reason,
        reprint,
        printer: selectedPrinter,
        lines,
      });
    },
    onSuccess: (result) => {
      setOpen(false);
      setError(null);
      const printed = result.reprinted_subcontainers.length > 0
        ? ` Reprinted ${String(result.reprinted_subcontainers.length)} container label${result.reprinted_subcontainers.length === 1 ? "" : "s"}.`
        : "";
      const printProblem = result.print_error != null ? ` ${result.print_error}` : "";
      onSaved(`Receipt quantities updated.${printed}${printProblem}`);
    },
    onError: (overrideError: unknown) => {
      setError(overrideError instanceof ApiError ? overrideError.message : "Could not override the receipt.");
    },
  });

  if (editableLines.length === 0) return null;

  return (
    <div className="space-y-2">
      {open ? (
        <div className="space-y-3 rounded-md border border-slate-200 bg-white p-3">
          <p className="text-sm text-slate-700">
            Correct the received quantity when the count used the wrong unit. This records who made the change and why.
          </p>
          <ul className="space-y-2">
            {editableLines.map((line) => {
              const itemId = line.receipt_item_id ?? "";
              return (
                <li key={itemId} className="grid grid-cols-[minmax(0,1fr)_8rem] items-center gap-2">
                  <span className="min-w-0 text-sm">
                    <span className="font-mono">{line.sku}</span>
                    <span className="ml-2 text-slate-500">
                      now {formatQuantityWithUnit(formatReceiptDiscrepancyQuantity(line.quantity), line.unit_of_measure)}
                    </span>
                  </span>
                  <Input
                    aria-label={`New quantity for ${line.sku}`}
                    inputMode="decimal"
                    value={drafts[itemId] ?? String(line.quantity)}
                    onChange={(event) =>
                      setDrafts((current) => ({ ...current, [itemId]: event.target.value }))
                    }
                  />
                </li>
              );
            })}
          </ul>
          <div>
            <Label htmlFor={`receipt-override-reason-${receipt.id}`}>Reason</Label>
            <textarea
              id={`receipt-override-reason-${receipt.id}`}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={2}
              className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
              placeholder="Counted buckets, not pounds"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={reprint}
              onChange={(event) => setReprint(event.target.checked)}
            />
            Reprint container labels
          </label>
          {reprint ? (
            <div>
              <ZplLocationSelect
                id={`receipt-override-printer-${receipt.id}`}
                value={station}
                onChange={setStation}
                unavailableMessage={unavailableMessage}
              />
            </div>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={mutation.isPending}
              onClick={() => {
                setOpen(false);
                setError(null);
              }}
            >
              Cancel
            </Button>
            <Button type="button" size="sm" isLoading={mutation.isPending} onClick={() => mutation.mutate()}>
              Save override
            </Button>
          </div>
        </div>
      ) : (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => {
            const nextDrafts: Record<string, string> = {};
            for (const line of editableLines) {
              if (line.receipt_item_id != null) nextDrafts[line.receipt_item_id] = String(line.quantity);
            }
            setDrafts(nextDrafts);
            setOpen(true);
            setError(null);
          }}
        >
          Override receipt
        </Button>
      )}
      {error ? (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function PurchaseOrderQuantityResolve({
  purchaseOrderId,
  onResolved,
}: {
  purchaseOrderId: string;
  onResolved: (message: string) => void;
}): JSX.Element {
  const [mode, setMode] = useState<"allow" | "refuse" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: () =>
      mode === "refuse"
        ? refuseReceiptDiscrepancyExcessPayment(purchaseOrderId)
        : allowReceiptDiscrepancyExcessMaterial(purchaseOrderId),
    onSuccess: (result) => {
      const chosen = mode;
      setMode(null);
      setError(null);
      const created = result.gipi_created > 0
        ? " The purchase invoice was created."
        : result.gipi_error != null
          ? ` The purchase invoice was not created. ${result.gipi_error}`
          : " The purchase invoice was not created yet.";
      onResolved(
        chosen === "refuse"
          ? `Payment for the excess was refused. The purchase order stays the same, and the purchase invoice uses the billed quantity at a lower unit price.${created}`
          : `Excess material was allowed. The purchase order stays the same, and the purchase invoice uses the billed quantity.${created}`,
      );
    },
    onError: (resolveError: unknown) => {
      setError(resolveError instanceof ApiError ? resolveError.message : "Could not resolve this discrepancy.");
    },
  });

  return (
    <div className="space-y-2">
      <p className="text-sm text-slate-700">
        The vendor invoice is above the purchase order. The purchase order quantity does not change.
      </p>
      {mode == null ? (
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" onClick={() => setMode("allow")}>
            Allow excess material
          </Button>
          <Button type="button" size="sm" variant="secondary" onClick={() => setMode("refuse")}>
            Refuse payment for excess material
          </Button>
        </div>
      ) : (
        <div className="space-y-2 rounded-md border border-slate-200 bg-white p-2">
          <p className="text-sm text-slate-700">
            {mode === "allow"
              ? "The purchase invoice is entered at the billed quantity, which is greater than the purchase order. The unit price stays the billed price."
              : "The purchase invoice is entered at the billed quantity with a lower unit price, so the line amount matches the purchase order."}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={mutation.isPending}
              onClick={() => {
                setMode(null);
                setError(null);
              }}
            >
              Cancel
            </Button>
            <Button type="button" size="sm" isLoading={mutation.isPending} onClick={() => mutation.mutate()}>
              {mode === "allow" ? "Allow excess material" : "Refuse payment for excess material"}
            </Button>
          </div>
        </div>
      )}
      {error ? (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function AcceptOverageButton({
  invoiceId,
  onAccepted,
}: {
  invoiceId: string;
  onAccepted: () => void;
}): JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: () => approveReceiptOverageAsFree(invoiceId),
    onSuccess: () => {
      setConfirming(false);
      setError(null);
      onAccepted();
    },
    onError: (approveError: unknown) => {
      setError(approveError instanceof ApiError ? approveError.message : "Could not accept the overage.");
    },
  });

  return (
    <div className="space-y-2">
      {confirming ? (
        <div className="space-y-2 rounded-md border border-slate-200 bg-white p-2">
          <p className="text-sm text-slate-700">
            Creates a purchase invoice (GIPI) only up to submitted Goods IN received per line. Where received is
            above the vendor invoice, the billed line total spreads across that received quantity (extra units are
            free). Lines not yet received are not invoiced above receipt.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={mutation.isPending}
              onClick={() => {
                setConfirming(false);
                setError(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              isLoading={mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              Accept overage as free
            </Button>
          </div>
        </div>
      ) : (
        <Button type="button" size="sm" variant="secondary" onClick={() => setConfirming(true)}>
          Accept overage as free
        </Button>
      )}
      {error ? (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      ) : null}
    </div>
  );
}

const DOCUMENT_PEEK_LINK_CLASS = "font-medium text-brand-700 underline-offset-2 hover:underline";

function VendorInvoicePeekLink({ invoiceId, label }: { invoiceId: string; label: string }): JSX.Element {
  const peek: EntityPeekProps = { kind: "vendor_invoice", invoiceId };
  const prefetchHandlers = useEntityPeekPrefetchHandlers(peek);
  return (
    <DetailLinkWithEntityPeek
      peek={peek}
      trigger={
        <Link
          to={`/accounting/ap/${encodeURIComponent(invoiceId)}/edit`}
          className={DOCUMENT_PEEK_LINK_CLASS}
          {...prefetchHandlers}
        >
          {label}
        </Link>
      }
    />
  );
}

function InboundReceiptPeekLink({ receiptId }: { receiptId: string }): JSX.Element {
  const peek: EntityPeekProps = { kind: "inbound_receipt", receiptId };
  const prefetchHandlers = useEntityPeekPrefetchHandlers(peek);
  return (
    <DetailLinkWithEntityPeek
      peek={peek}
      trigger={
        <Link
          to={buildInboundReceiptReviewPath(receiptId)}
          className={DOCUMENT_PEEK_LINK_CLASS}
          {...prefetchHandlers}
        >
          Receipt {receiptId}
        </Link>
      }
    />
  );
}

function WorkOrderBlock({ workOrder }: { workOrder: ReceiptDiscrepancyWorkOrder }): JSX.Element {
  const buildOrderId = Number(workOrder.build_order_id);
  return (
    <div className="space-y-1 text-sm">
      <WorkOrderDetailLink
        buildOrderId={buildOrderId}
        workOrderNumber={workOrder.work_order_number}
      />
      <div
        className={
          workOrder.differs
            ? "grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 rounded bg-amber-100/80 px-2 py-1"
            : "grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 px-2 py-1"
        }
      >
        <span className="font-mono text-slate-900">{workOrder.sku}</span>
        <span className="text-right tabular-nums text-slate-800">
          {formatQuantityWithUnit(formatReceiptDiscrepancyQuantity(workOrder.quantity), workOrder.unit_of_measure)}
          {workOrder.labour_cost != null
            ? ` · ${formatReceiptDiscrepancyMoney(workOrder.labour_cost, "USD")}`
            : ""}
        </span>
      </div>
    </div>
  );
}
